import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faSave, faSpinner, faCheck, faPlus, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/common/EditPackageDataModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';
import { CurrencyObject } from '../../services/utils.service';
import { fileCompressionService } from '../../services/fileCompression.service';

const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const day = String(tomorrow.getDate()).padStart(2, '0');
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const year = tomorrow.getFullYear();
    return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    const day = String(maxDate.getDate()).padStart(2, '0');
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const year = maxDate.getFullYear();
    return `${year}-${month}-${day}`;
};


interface EditPackageDataModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (updatedData: any) => Promise<void>;
    initialData: {
        width: number | "";
        height: number | "";
        length: number | "";
        weight: number | "";
        fob: number | "";
        unitSystem: "metric" | "imperial";
        parcelOrdDoc: string;
        currency: string;
        packageContent: string;
        pickupDate?: string;
        hasBattery: boolean;
        selectedDocument: { file: File; base64string: string; ext: string } | null;
    };
    currencies: CurrencyObject[];
    isDropOff?: boolean;
}

const EditPackageDataModal: React.FC<EditPackageDataModalProps> = ({
    isOpen,
    onClose,
    onSave,
    initialData,
    currencies,
    isDropOff = false
}) => {
    const [values, setValues] = useState(initialData);
    const [errors, setErrors] = useState<any>({});
    const [isSaving, setIsSaving] = useState(false);
    const [docErrors, setDocErrors] = useState<string>("");
    const [isCompressingDoc, setIsCompressingDoc] = useState<boolean>(false);

    // Sync state with props when modal opens
    useEffect(() => {
        if (isOpen) {
            setValues(initialData);
            setErrors({});
            setDocErrors("");
        }
    }, [isOpen, initialData]);

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;
        if (name === "unitSystem") {
            setValues(prev => ({
                ...prev,
                unitSystem: value,
                width: "",
                height: "",
                length: "",
                weight: ""
            }));
            setErrors((prev: any) => ({
                ...prev,
                unitSystem: null,
                width: null,
                height: null,
                length: null,
                weight: null
            }));
        } else {
            setValues(prev => ({ ...prev, [name]: value }));
            if (errors[name]) {
                setErrors((prev: any) => ({ ...prev, [name]: null }));
            }
        }
    };

    const handleDocumentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const file = e.target.files[0];
        if (!file) return;

        const allowedExtensions = ["png", "jpg", "jpeg", "pdf"];
        const fileExt = file.name.split('.').pop()?.toLowerCase() || "";
        if (!allowedExtensions.includes(fileExt)) {
            setDocErrors(`El archivo "${file.name}" tiene una extensión no permitida. Solo se aceptan png, jpg, jpeg, pdf.`);
            return;
        }

        const maxSizeBytes = fileExt === "pdf" ? 500 * 1024 : 5 * 1024 * 1024;
        const maxWeightLabel = fileExt === "pdf" ? "500KB" : "5MB";

        if (file.size > maxSizeBytes) {
            setDocErrors(`El archivo "${file.name}" supera el peso máximo de ${maxWeightLabel}.`);
            return;
        }

        try {
            setIsCompressingDoc(true);
            setDocErrors("");

            const result = await fileCompressionService.compressFile(file, {
                maxWidth: 1920,
                maxHeight: 1920,
                quality: 0.8,
            });

            setValues(prev => ({
                ...prev,
                selectedDocument: {
                    file: result.file,
                    base64string: result.base64string,
                    ext: result.ext,
                }
            }));
            setErrors((prev: any) => ({ ...prev, document: null }));
        } catch (err) {
            console.error("Error al procesar/comprimir el archivo:", err);
            setDocErrors("Error al procesar el archivo.");
        } finally {
            setIsCompressingDoc(false);
            e.target.value = "";
        }
    };

    const removeDocument = () => {
        setValues(prev => ({ ...prev, selectedDocument: null }));
        setDocErrors("");
    };

    const validate = () => {
        const newErrors: any = {};
        if (!values.width || Number(values.width) <= 0) newErrors.width = "Requerido y > 0";
        if (!values.height || Number(values.height) <= 0) newErrors.height = "Requerido y > 0";
        if (!values.length || Number(values.length) <= 0) newErrors.length = "Requerido y > 0";
        if (!values.weight || Number(values.weight) <= 0) newErrors.weight = "Requerido y > 0";
        if (!values.fob || Number(values.fob) <= 0) newErrors.fob = "Requerido y > 0";
        if (!values.packageContent) {
            newErrors.packageContent = "La descripción es requerida";
        } else if (values.packageContent.length > 255) {
            newErrors.packageContent = "Máximo 255 caracteres";
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(values);
            onClose();
        } catch (error) {
            console.error("Error saving package data:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleUnitSystemChange = (system: "metric" | "imperial") => {
        setValues(prev => ({
            ...prev,
            unitSystem: system,
            width: "",
            height: "",
            length: "",
            weight: ""
        }));
        setErrors((prev: any) => ({
            ...prev,
            width: null,
            height: null,
            length: null,
            weight: null
        }));
    };

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            centered
            size="lg"
            className={styles.editPackageDataModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title className={`${styles.modalTitle} ${modalStyles.modalTitle}`}>
                    <FontAwesomeIcon icon={faBox} /> Editar Detalles del Paquete
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.editPackageDataModalBody}>
                <Form>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                        <div className={styles.switchContainer}>
                            <button
                                type="button"
                                onClick={() => handleUnitSystemChange('imperial')}
                                className={`${styles.switchButton} ${values.unitSystem === 'imperial' ? styles.switchButtonActive : ''}`}
                            >
                                Imperial
                            </button>
                            <button
                                type="button"
                                onClick={() => handleUnitSystemChange('metric')}
                                className={`${styles.switchButton} ${values.unitSystem === 'metric' ? styles.switchButtonActive : ''}`}
                            >
                                Métrico
                            </button>
                        </div>
                    </div>

                    <Row className="mb-3">
                        <Col md={12} sm={12} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Tipo de Paquete</Form.Label>
                                <div className={styles.checkboxContainer}>
                                    <div
                                        className={`${styles.checkboxCard} ${values.parcelOrdDoc === 'PARCEL' ? styles.checkboxCardActive : ''}`}
                                        onClick={() => {
                                            setValues(prev => ({ ...prev, parcelOrdDoc: 'PARCEL' }));
                                            setErrors({});
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={values.parcelOrdDoc === 'PARCEL'}
                                            readOnly
                                            className={styles.checkboxInput}
                                        />
                                        <span className={styles.checkboxLabel}>Caja</span>
                                    </div>
                                    <div
                                        className={`${styles.checkboxCard} ${values.parcelOrdDoc === 'DOC' ? styles.checkboxCardActive : ''}`}
                                        onClick={() => {
                                            setValues(prev => ({ ...prev, parcelOrdDoc: 'DOC', hasBattery: false }));
                                            setErrors({});
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={values.parcelOrdDoc === 'DOC'}
                                            readOnly
                                            className={styles.checkboxInput}
                                        />
                                        <span className={styles.checkboxLabel}>Documento</span>
                                    </div>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="width"
                                    placeholder="Ej: 10"
                                    value={values.width}
                                    onChange={handleChange}
                                    isInvalid={!!errors.width}
                                    className={styles.input}
                                />
                                <Form.Control.Feedback type="invalid">{errors.width}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Alto ({values.unitSystem === "metric" ? "cm" : "in"})</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="height"
                                    placeholder="Ej: 10"
                                    value={values.height}
                                    onChange={handleChange}
                                    isInvalid={!!errors.height}
                                    className={styles.input}
                                />
                                <Form.Control.Feedback type="invalid">{errors.height}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Largo ({values.unitSystem === "metric" ? "cm" : "in"})</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="length"
                                    placeholder="Ej: 10"
                                    value={values.length}
                                    onChange={handleChange}
                                    isInvalid={!!errors.length}
                                    className={styles.input}
                                />
                                <Form.Control.Feedback type="invalid">{errors.length}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Peso ({values.unitSystem === "metric" ? "kg" : "lbs"})</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="weight"
                                    placeholder="Ej: 1"
                                    value={values.weight}
                                    onChange={handleChange}
                                    isInvalid={!!errors.weight}
                                    className={styles.input}
                                />
                                <Form.Control.Feedback type="invalid">{errors.weight}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>FOB ({values.currency})</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="fob"
                                    placeholder="Ej: 100"
                                    value={values.fob}
                                    onChange={handleChange}
                                    isInvalid={!!errors.fob}
                                    className={styles.input}
                                />
                                <Form.Control.Feedback type="invalid">{errors.fob}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4} sm={4} xs={12}>
                            <Form.Group>
                                <Form.Label className={styles.label}>Moneda</Form.Label>
                                <Form.Select name="currency" value={values.currency} onChange={handleChange} className={styles.select}>
                                    {currencies.map((curr) => (
                                        <option key={curr.id} value={curr.isO3Currency}>
                                            {curr.isO3Currency}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {!isDropOff && (
                        <Row className="mb-3">
                            <Col md={12} sm={12} xs={12}>
                                <Form.Group>
                                    <Form.Label className={styles.label}>Fecha de Recolección (Deseada)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="pickupDate"
                                        value={values.pickupDate || ""}
                                        onChange={handleChange}
                                        min={getTomorrowDate()}
                                        max={getMaxDate()}
                                        className={styles.input}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    {/* Baterías Checkbox */}
                    <div className="mb-3">
                        <div
                            className={styles.checkboxRow}
                            style={{ opacity: values.parcelOrdDoc === "DOC" ? 0.6 : 1, cursor: values.parcelOrdDoc === "DOC" ? "not-allowed" : "pointer" }}
                            onClick={() => { if (values.parcelOrdDoc !== "DOC") setValues(prev => ({ ...prev, hasBattery: !prev.hasBattery })); }}
                        >
                            <input type="checkbox" name="hasBattery" checked={values.hasBattery}
                                disabled={values.parcelOrdDoc === "DOC"}
                                onChange={(e) => { e.stopPropagation(); if (values.parcelOrdDoc !== "DOC") setValues(prev => ({ ...prev, hasBattery: e.target.checked })); }} />
                            <span>El paquete contiene baterías (ej. Laptops, teléfonos, etc.)</span>
                        </div>
                    </div>

                    {/* Documentación de Respaldo */}
                    <div className="mb-3" style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '16px', backgroundColor: '#f8fafc' }}>
                        <Form.Label className={styles.label} style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: '8px' }}>
                            Documentación de Respaldo (Opcional)
                        </Form.Label>
                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '12px' }}>
                            Cargue un archivo de respaldo (PNG/JPG máx 5MB, PDF máx 500KB).
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 16px',
                                backgroundColor: '#0f172a',
                                color: '#ffffff',
                                borderRadius: '8px',
                                cursor: (values.selectedDocument || isCompressingDoc) ? 'not-allowed' : 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                opacity: (values.selectedDocument || isCompressingDoc) ? 0.6 : 1,
                                userSelect: 'none',
                                transition: 'background-color 0.2s'
                            }}>
                                <FontAwesomeIcon icon={isCompressingDoc ? faSpinner : faPlus} spin={isCompressingDoc} style={{ marginRight: '6px' }} />
                                {isCompressingDoc ? "Procesando..." : "Seleccionar Archivo"}
                                <input
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    onChange={handleDocumentChange}
                                    disabled={!!values.selectedDocument || isCompressingDoc}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                                {isCompressingDoc ? "Comprimiendo archivo..." : values.selectedDocument ? "1 de 1 archivo cargado" : "0 de 1 archivo cargado"}
                            </span>
                        </div>

                        {docErrors && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '8px', fontWeight: 500 }}>
                                {docErrors}
                            </div>
                        )}
                        {errors.document && (
                            <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '8px', fontWeight: 500 }}>
                                {errors.document}
                            </div>
                        )}

                        {values.selectedDocument && (
                            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem'
                                }}>
                                    <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '80%', color: '#334155', fontWeight: 500 }}>
                                        {values.selectedDocument.file.name} ({(values.selectedDocument.file.size / (1024 * 1024)).toFixed(2)} MB)
                                    </span>
                                    <button
                                        type="button"
                                        onClick={removeDocument}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            padding: '2px 6px',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        &times;
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <Form.Group className="mb-0">
                        <Form.Label className={styles.label}>Descripción del Contenido *</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="packageContent"
                            rows={3}
                            maxLength={255}
                            value={values.packageContent}
                            onChange={handleChange}
                            isInvalid={!!errors.packageContent}
                            className={styles.textarea}
                            style={{ resize: 'none' }}
                        />
                        <Form.Control.Feedback type="invalid">{errors.packageContent}</Form.Control.Feedback>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <button className={styles.secondaryBtn} onClick={onClose} disabled={isSaving}>
                    Cancelar
                </button>
                <Button
                    className={modalStyles.closeBtn}
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <><FontAwesomeIcon icon={faSpinner} spin /> Guardando...</>
                    ) : (
                        <><FontAwesomeIcon icon={faCheck} /> Guardar</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditPackageDataModal;
